import React, { useState } from "react";
import MarketingShell from "@/components/marketing/MarketingShell";
import { submitDemo } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import SoftImage from "@/components/SoftImage";

export default function RequestDemo() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", role: "", message: "" });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Please enter your name and email"); return; }
    setLoading(true);
    try { const res = await submitDemo(form); setDone(true); toast.success(res.message); }
    catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <MarketingShell>
      <section className="mx-auto max-w-[1180px] px-5 lg:px-8 py-14">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-[12px] font-bold tracking-[0.22em] text-[#5b6b86] uppercase">Request a Demo</p>
            <h1 className="font-display mt-3 text-[40px] font-extrabold text-[#10233f] leading-tight">See ProactiveBH <span className="text-gradient-blue">in action</span></h1>
            <p className="mt-4 text-[17px] text-slate-500">Get a personalized walkthrough of our pediatric behavioral health platform. We'll tailor it to your clinic, families, and care teams.</p>
            <ul className="mt-6 space-y-3">
              {["HIPAA-compliant telehealth & EHR", "Parent & school collaboration portals", "AI-driven assessments & analytics"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-[15px] text-[#10233f] font-medium"><CheckCircle2 size={20} className="text-[#37a862]" /> {t}</li>
              ))}
            </ul>
            <div className="mt-8"><SoftImage src="/generated/coaching1.png" alt="Family" className="h-56 w-full rounded-[24px]" /></div>
          </div>
          <div className="card-soft card-soft-lg p-8">
            {done ? (
              <div className="text-center py-14">
                <div className="mx-auto h-16 w-16 rounded-full bg-[#e6f7ec] grid place-items-center text-[#37a862]"><CheckCircle2 size={34} /></div>
                <h3 className="font-display mt-5 text-[24px] font-extrabold text-[#10233f]">Thank you!</h3>
                <p className="mt-2 text-slate-500">Our team will reach out shortly to schedule your personalized demo.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <h3 className="font-display text-[22px] font-bold text-[#10233f]">Tell us about you</h3>
                {[["name", "Full name", "Jane Doe"], ["email", "Work email", "jane@clinic.com"], ["organization", "Organization", "Sunrise Pediatrics"], ["role", "Your role", "Clinical Director"]].map(([k, label, ph]) => (
                  <div key={k}>
                    <label className="text-sm font-semibold text-[#10233f]">{label}</label>
                    <input data-testid={`demo-${k}`} value={form[k]} onChange={set(k)} placeholder={ph} className="mt-1.5 w-full card-soft px-4 py-3 text-[15px] outline-none" style={{ borderRadius: 14 }} />
                  </div>
                ))}
                <div>
                  <label className="text-sm font-semibold text-[#10233f]">What are you hoping to solve?</label>
                  <textarea data-testid="demo-message" value={form.message} onChange={set("message")} rows={3} className="mt-1.5 w-full card-soft px-4 py-3 text-[15px] outline-none resize-none" style={{ borderRadius: 14 }} />
                </div>
                <button data-testid="demo-submit" disabled={loading} className="btn-primary w-full py-3.5 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">{loading ? "Sending…" : (<>Request Demo <ArrowRight size={18} /></>)}</button>
              </form>
            )}
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
